// Вкладка «Люди»: список, поиск и карточка человека с выдачей доступа,
// заметкой и журналом. Всё в одном месте — открыл человека и видишь про него всё.
import { useEffect, useState } from 'react'

import { call, productTitle, relativeTime, shortDate, shortDateTime, visitGrid } from './adminApi'
import type { AdminPerson, AdminPersonCard } from './adminApi'

/** Что можно выдать руками. Дни есть только у подписки. */
const GRANTS: { label: string; product: string; days?: number }[] = [
  { label: 'Полный Атлас', product: 'atlas_full' },
  { label: 'Подписка 30 дней', product: 'atlas_sub_30d', days: 30 },
  { label: 'Подписка 90 дней', product: 'atlas_sub_30d', days: 90 },
  { label: 'Подписка на год', product: 'atlas_sub_30d', days: 365 },
  { label: 'Отчёт пары', product: 'atlas_couple_report' },
]

const REVOKES: { label: string; what: 'full' | 'sub' | 'couple_report'; ask: string }[] = [
  {
    label: 'Убрать Полный Атлас',
    what: 'full',
    ask: 'Убрать Полный Атлас? Доступ закроется, даже если человек купил его за деньги.',
  },
  {
    label: 'Убрать подписку',
    what: 'sub',
    ask: 'Убрать подписку? «Сегодня» и вопросы закроются сразу, даже если подписка оплачена.',
  },
  { label: 'Убрать отчёт пары', what: 'couple_report', ask: 'Убрать подаренный отчёт пары? Оплаченные не трогаем.' },
]

const TIME_ACCURACY: Record<string, string> = {
  exact: 'время точное',
  approx: 'время примерное',
  unknown: 'время неизвестно',
}

function personName(person: AdminPerson): string {
  if (person.forgottenAt) return 'забытый человек'
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ')
  return name || 'без имени'
}

/** Что у человека открыто — коротко, чтобы читалось прямо в списке. */
function accessBadges(person: AdminPerson): { text: string; tone: string }[] {
  const badges: { text: string; tone: string }[] = []
  if (person.forgottenAt) badges.push({ text: 'забыт', tone: 'gone' })
  if (person.fullAtlas) badges.push({ text: 'полный', tone: 'gold' })
  if (person.subscribedUntil && new Date(person.subscribedUntil) > new Date()) {
    badges.push({ text: `подписка до ${shortDate(person.subscribedUntil)}`, tone: 'gold' })
  }
  if (person.paidRub > 0) badges.push({ text: `${person.paidRub} ₽`, tone: 'plain' })
  return badges
}

export function AdminPeople() {
  const [people, setPeople] = useState<AdminPerson[]>([])
  const [search, setSearch] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [card, setCard] = useState<AdminPersonCard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = (query: string): void => {
    setLoading(true)
    call<{ users: AdminPerson[] }>(`/api/admin/users?q=${encodeURIComponent(query)}`)
      .then((data) => setPeople(data.users))
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Не вышло'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load('')
  }, [])

  const open = (person: AdminPerson): void => {
    if (openId === person.id) {
      setOpenId(null)
      setCard(null)
      return
    }
    setOpenId(person.id)
    setCard(null)
    call<AdminPersonCard>(`/api/admin/users/${person.id}`)
      .then(setCard)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Не вышло'))
  }

  /** Карточка обновилась — обновляем и строку списка, чтобы бейджи не врали. */
  const applyCard = (fresh: AdminPersonCard): void => {
    setCard(fresh)
    setPeople((rows) => rows.map((row) => (row.id === fresh.person.id ? fresh.person : row)))
  }

  return (
    <>
      <div className="admin-search">
        <input
          className="admin-input"
          placeholder="имя, @ник или telegram id"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') load(search)
          }}
        />
        <button type="button" className="admin-btn" onClick={() => load(search)}>
          Найти
        </button>
      </div>
      {error && <div className="field-warn">{error}</div>}

      <div className="person-legend">
        <span>{people.length} чел.</span>
        <span>заходы: сегодня / неделя / всего</span>
      </div>

      <div className="rowlist">
        {people.map((person) => (
          <div key={person.id}>
            <button type="button" className="rowitem person-row" onClick={() => open(person)}>
              <div>
                <div className="ttl">
                  {personName(person)} {person.username ? <span className="dimmed">@{person.username}</span> : null}
                </div>
                <div className="note">
                  {accessBadges(person).map((badge) => (
                    <span className={`person-badge ${badge.tone}`} key={badge.text}>
                      {badge.text}
                    </span>
                  ))}
                  {person.hasPortrait ? 'портрет собран' : person.hasProfile ? 'карта есть' : 'без карты'}
                </div>
              </div>
              <div className="right person-right">
                <div>{relativeTime(person.lastSeenAt)}</div>
                <div className="dimmed">
                  {person.visitsToday} / {person.visitsWeek} / {person.visitsTotal}
                </div>
              </div>
            </button>
            {openId === person.id && (
              <PersonCard
                card={card}
                onCard={applyCard}
                onForgotten={() => {
                  setOpenId(null)
                  setCard(null)
                  load(search)
                }}
              />
            )}
          </div>
        ))}
        {!loading && people.length === 0 && <div className="state">никого не нашлось</div>}
        {loading && <div className="state">…</div>}
      </div>
    </>
  )
}

function PersonCard(props: {
  card: AdminPersonCard | null
  onCard: (card: AdminPersonCard) => void
  onForgotten: () => void
}) {
  const { card } = props
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const [noteSaved, setNoteSaved] = useState(false)
  const [error, setError] = useState('')

  // Заметку подставляем только при смене человека: иначе набранный, но ещё не
  // сохранённый текст затрётся, стоит нажать любую кнопку выдачи.
  useEffect(() => {
    if (card) setNote(card.person.adminNote)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.person.id])

  if (!card) return <div className="state">…</div>
  const { person, profile, counts, purchases, visitDays, events, couples } = card

  const act = (path: string, body: unknown): void => {
    setBusy(true)
    setError('')
    call<{ card?: AdminPersonCard }>(path, body)
      .then((result) => {
        if (result.card) props.onCard(result.card)
      })
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Не вышло'))
      .finally(() => setBusy(false))
  }

  const maxVisits = Math.max(1, ...visitDays.map((day) => day.count))

  return (
    <div className="person-card">
      <div className="person-block">
        <div className="lbl">Кто это</div>
        <div className="person-facts">
          <div>telegram id: {person.forgottenAt ? 'стёрт' : person.telegramId}</div>
          <div>завёлся {shortDate(person.createdAt)}</div>
          {person.forgottenAt && <div>забыт {shortDate(person.forgottenAt)} — данные стёрты</div>}
          {profile ? (
            <div>
              {new Date(profile.birthDate).toLocaleDateString('ru-RU')}
              {profile.birthTime ? `, ${profile.birthTime.slice(0, 5)}` : ''} ·{' '}
              {TIME_ACCURACY[profile.timeAccuracy] ?? profile.timeAccuracy}
              {profile.placeLabel ? ` · ${profile.placeLabel}` : ''}
            </div>
          ) : (
            <div>карту рождения не заводил</div>
          )}
          <div>
            карточек дня {counts.dayCards} · вопросов {counts.oracleQuestions} · глав {counts.chapters} · PDF{' '}
            {counts.exports}
            {couples.length > 0 ? ` · пар ${couples.length}` : ''}
          </div>
        </div>
      </div>

      <div className="person-block">
        <div className="lbl">Доступ</div>
        <div className="person-facts">
          <div>Полный Атлас: {person.fullAtlas ? 'есть' : 'нет'}</div>
          <div>
            Подписка:{' '}
            {person.subscribedUntil && new Date(person.subscribedUntil) > new Date()
              ? `до ${shortDate(person.subscribedUntil)}`
              : 'нет'}
          </div>
        </div>
        {/* Забытому выдавать нечего: строка обезличена, доступ к ней уже никого не касается. */}
        {!person.forgottenAt && (
        <div className="person-actions">
          {GRANTS.map((grant) => (
            <button
              type="button"
              className="admin-chip"
              key={grant.label}
              disabled={busy}
              onClick={() =>
                act(`/api/admin/users/${person.id}/grant`, { product: grant.product, days: grant.days ?? 30 })
              }
            >
              + {grant.label}
            </button>
          ))}
          {REVOKES.map((revoke) => (
            <button
              type="button"
              className="admin-chip off"
              key={revoke.what}
              disabled={busy}
              onClick={() => {
                if (!window.confirm(revoke.ask)) return
                act(`/api/admin/users/${person.id}/revoke`, { what: revoke.what })
              }}
            >
              − {revoke.label}
            </button>
          ))}
        </div>
        )}
        {!person.forgottenAt && (
          <div className="note">
            Отчёт пары записывается покупкой, но сам раздел в продукте ещё не открыт.
          </div>
        )}
      </div>

      <div className="person-block">
        <div className="lbl">Заходы за две недели</div>
        <div className="person-bars">
          {visitGrid(visitDays).map((day) => (
            <div className="person-bar" key={day.day} title={`${day.day}: ${day.count}`}>
              <div
                className="person-bar-fill"
                style={{ height: day.count === 0 ? 0 : `${Math.round((day.count / maxVisits) * 100)}%` }}
              />
            </div>
          ))}
        </div>
        <div className="note">
          сегодня {person.visitsToday} · за неделю {person.visitsWeek} · всего {person.visitsTotal}
        </div>
      </div>

      <div className="person-block" style={{ display: person.forgottenAt ? 'none' : undefined }}>
        <div className="lbl">Заметка</div>
        <textarea
          className="admin-note-field"
          rows={2}
          placeholder="что важно помнить про этого человека"
          value={note}
          onChange={(event) => {
            setNote(event.target.value)
            setNoteSaved(false)
          }}
        />
        <button
          type="button"
          className="admin-chip"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            call(`/api/admin/users/${person.id}/note`, { note })
              .then(() => setNoteSaved(true))
              .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Не вышло'))
              .finally(() => setBusy(false))
          }}
        >
          {noteSaved ? 'сохранено' : 'Сохранить заметку'}
        </button>
      </div>

      {purchases.length > 0 && (
        <div className="person-block">
          <div className="lbl">Покупки</div>
          <div className="person-facts">
            {purchases.map((purchase, index) => (
              <div key={`${purchase.createdAt}-${index}`}>
                {productTitle(purchase.productKey)} · {purchase.manual ? 'подарок' : `${purchase.amountRub} ₽`} ·{' '}
                {purchase.status} · {shortDate(purchase.createdAt)}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="person-block">
        <div className="lbl">Журнал</div>
        <div className="person-log">
          {events.map((event, index) => (
            <div className="person-log-row" key={`${event.at}-${index}`}>
              <div className="person-log-time">{shortDateTime(event.at)}</div>
              <div>
                <div className="person-log-title">{event.title}</div>
                {event.note && <div className="dimmed">{event.note}</div>}
              </div>
            </div>
          ))}
          {events.length === 0 && <div className="dimmed">следов пока нет</div>}
        </div>
      </div>

      {error && <div className="field-warn">{error}</div>}

      {!person.forgottenAt && (
        <button
          type="button"
          className="admin-chip danger"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Забыть ${personName(person)}? Все его данные и PDF будут стёрты.`)) return
            if (!window.confirm('Это необратимо. Платежи останутся в отчётности. Точно забыть?')) return
            setBusy(true)
            call<{ warning?: string }>(`/api/admin/users/${person.id}/forget`, { confirm: 'ЗАБЫТЬ' })
              .then((result) => {
                if (result.warning) window.alert(result.warning)
                props.onForgotten()
              })
              .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Не вышло'))
              .finally(() => setBusy(false))
          }}
        >
          Забыть человека
        </button>
      )}
    </div>
  )
}
